import { AxiosError } from 'axios';
import apiClient from '@/services/api';
import type { CurrentDefinition, DraftView, ProblemView, RegistryDefinition } from './registry';

/**
 * The registry's operator endpoints, as the taxonomy and flows screens use them (ported from
 * Veramenta's lib/api.ts). The flow impact, flow publish and rollout endpoints of Veramenta's CRM
 * boards are not here: GeoNexora has no boards, so a flow is published like any other change,
 * through the registry draft.
 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: unknown) {
    super(message);
  }

  /** The problems the server listed, each naming where: `gx.platform.a101.shape`. */
  get problems(): ProblemView[] {
    const listed = (this.body as { problems?: ProblemView[] } | null)?.problems;
    return Array.isArray(listed) ? listed : [];
  }
}

function failed(e: unknown, what: string): ApiError {
  const error = e as AxiosError<{ message?: string; detail?: string; title?: string; error?: string }>;
  const status = error.response?.status ?? 0;
  const body = error.response?.data ?? null;
  const said = body && typeof body === 'object' ? body.message || body.detail || body.title || body.error : undefined;
  return new ApiError(said || `${what}: ${error.message}`, status, body);
}

async function call<T>(what: string, run: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await run()).data;
  } catch (e) {
    throw failed(e, what);
  }
}

const BASE = '/v1/operator/taxonomy/registry';

export const api = {
  /** The published definition. */
  registryDefinition: (): Promise<CurrentDefinition> =>
    call('GET registry', () => apiClient.get<CurrentDefinition>(BASE)),

  /** The draft being worked on, with its definition, or null when there is none (404). */
  registryDraft: async (): Promise<(DraftView & { definition: RegistryDefinition }) | null> => {
    try {
      return await call('GET draft', () => apiClient.get<DraftView & { definition: RegistryDefinition }>(`${BASE}/draft`));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },

  /** Saves the draft and says what it would change; refused with each problem when it breaks a rule. */
  saveRegistryDraft: (definition: RegistryDefinition): Promise<DraftView> =>
    call('PUT draft', () => apiClient.put<DraftView>(`${BASE}/draft`, definition)),

  discardRegistryDraft: async (): Promise<void> => {
    try {
      await apiClient.delete(`${BASE}/draft`);
    } catch (e) {
      const error = failed(e, 'DELETE draft');
      if (error.status !== 404) throw error;
    }
  },

  /** The draft becomes the next version. */
  publishRegistry: (): Promise<{ version: number; contentHash: string; publishedAt: string }> =>
    call('POST publish', () => apiClient.post(`${BASE}/draft/publish`)),
};
